// src/pages/LegalNotice.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

const OPERATOR = {
  platformName: "ZEDRO Academy",
  legalTradeName: "Académie ZEDRO",
  website: "zedro.academy",
  supportEmail: "support@zedro.academy",
  adminEmail: "admin@zedro.academy",
  addressEs:
    "9165 Rue Lennon, Suite 412, Brossard, Québec, Canadá, J4Y 0R9",
  addressFr:
    "9165, rue Lennon, suite 412, Brossard, Québec, Canada, J4Y 0R9",
  jurisdictionEs: "Québec, Canadá",
  jurisdictionFr: "Québec, Canada",
};

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const pageStyle = {
  maxWidth: 900,
  margin: "2rem auto",
  padding: "0 1rem",
  fontFamily: "Questrial, sans-serif",
  lineHeight: 1.75,
  color: "#2c3b51",
};

const sectionTitleStyle = {
  color: "#286b94",
};

const LEGAL_NOTICE_COPY = {
  es: {
    title: "Aviso legal",
    lastUpdatedLabel: "Última actualización:",
    lastUpdated: "28 de junio de 2026",
    intro: [
      <>
        Este sitio web, sus contenidos, funcionalidades, membresías, recursos y
        servicios digitales forman parte de <strong>{OPERATOR.platformName}</strong>,
        plataforma operada bajo el nombre comercial registrado{" "}
        <strong>{OPERATOR.legalTradeName}</strong> en {OPERATOR.jurisdictionEs}.
      </>,
      <>
        El acceso y uso de <strong>{OPERATOR.website}</strong> implica la
        aceptación de este Aviso legal, así como de los Términos y Condiciones,
        la Política de Privacidad, la Política de Cookies y demás documentos
        legales publicados en la plataforma.
      </>,
    ],
    sections: [
      {
        title: "1. Identificación del sitio y del operador",
        paragraphs: [
          <>
            Nombre público de la plataforma: <strong>{OPERATOR.platformName}</strong>
          </>,
          <>
            Nombre comercial registrado: <strong>{OPERATOR.legalTradeName}</strong>
          </>,
          <>
            Dirección comercial/postal: <strong>{OPERATOR.addressEs}</strong>
          </>,
          <>
            Ubicación de operación: <strong>{OPERATOR.jurisdictionEs}</strong>
          </>,
          <>
            Sitio web: <strong>{OPERATOR.website}</strong>
          </>,
          <>
            Correo de soporte: <a href={`mailto:${OPERATOR.supportEmail}`}>{OPERATOR.supportEmail}</a>
          </>,
          <>
            Correo administrativo/legal: <a href={`mailto:${OPERATOR.adminEmail}`}>{OPERATOR.adminEmail}</a>
          </>,
          <>
            ZEDRO Academy utiliza estos canales oficiales para consultas
            relacionadas con acceso, membresía, pagos, cancelación, privacidad,
            soporte y asuntos legales.
          </>,
        ],
      },
      {
        title: "2. Propiedad intelectual",
        paragraphs: [
          <>
            Todo el contenido disponible en ZEDRO Academy, incluyendo textos,
            nombres, metodología, sistemas, módulos, estructura de formación,
            diseño visual, logotipos, imágenes, audios, recursos descargables,
            interfaces, arquitectura de navegación, software, código,
            materiales formativos y elementos de marca, está protegido por
            normas de propiedad intelectual y pertenece a ZEDRO Academy,
            Académie ZEDRO o a sus respectivos licenciantes.
          </>,
          <>
            Queda prohibida la copia, reproducción, modificación, distribución,
            publicación, venta, sublicencia, explotación comercial o uso no
            autorizado de los contenidos, metodología o materiales de ZEDRO
            Academy, salvo autorización previa, expresa y por escrito.
          </>,
        ],
      },
      {
        title: "3. Licencia de uso limitada",
        paragraphs: [
          <>
            El acceso a la plataforma, contenidos y recursos de ZEDRO Academy se
            concede bajo una licencia limitada, personal, no exclusiva,
            intransferible, revocable y destinada al uso individual del usuario
            dentro de la plataforma.
          </>,
          <>Esta licencia permite:</>,
        ],
        list: [
          "Acceder a los contenidos incluidos en una membresía activa.",
          "Usar los recursos para aprendizaje, reflexión y aplicación personal.",
          "Guardar progreso, respuestas y registros dentro de la cuenta del usuario.",
          "Descargar materiales únicamente cuando la plataforma lo permita expresamente.",
        ],
        afterList: [
          <>
            <strong>No está permitido:</strong>
          </>,
        ],
        secondList: [
          "Compartir credenciales de acceso con terceros.",
          "Revender, copiar o redistribuir contenidos de la plataforma.",
          "Usar la metodología, sistemas o materiales con fines comerciales, institucionales o formativos externos sin autorización escrita.",
          "Extraer, copiar o replicar la estructura de la plataforma para crear productos similares.",
          "Usar automatizaciones, scraping, ingeniería inversa o herramientas que afecten la seguridad o funcionamiento de la plataforma.",
        ],
      },
      {
        title: "4. Uso adecuado de la plataforma",
        paragraphs: [
          <>
            El usuario se compromete a utilizar ZEDRO Academy de forma legal,
            respetuosa, responsable y coherente con el propósito formativo de la
            plataforma.
          </>,
          <>No se permite:</>,
        ],
        list: [
          "Usar la plataforma con fines fraudulentos, abusivos, ofensivos o ilegales.",
          "Intentar acceder a cuentas, datos, sistemas o áreas no autorizadas.",
          "Introducir virus, malware, código malicioso o interferencias técnicas.",
          "Publicar o guardar contenido que vulnere derechos de terceros.",
          "Realizar acciones que comprometan la seguridad, estabilidad o disponibilidad de la plataforma.",
        ],
      },
      {
        title: "5. Idioma del sitio y del contenido formativo",
        paragraphs: [
          <>
            La información pública esencial, las condiciones de compra y las
            páginas legales de ZEDRO Academy pueden estar disponibles en español
            y francés.
          </>,
          <>
            El contenido formativo principal del ecosistema ZEDRO Academy está
            disponible inicialmente en español. Al iniciar una membresía, el
            usuario entiende que la experiencia formativa principal se ofrece
            inicialmente en español, aunque la información legal y comercial
            esencial pueda estar disponible también en francés.
          </>,
        ],
      },
      {
        title: "6. Contenido formativo y límites",
        paragraphs: [
          <>
            Los contenidos de ZEDRO Academy tienen finalidad formativa,
            educativa, reflexiva e informativa. No sustituyen asesoría médica,
            psicológica, legal, financiera, terapéutica, contable, pastoral
            personalizada ni ningún tipo de acompañamiento profesional que el
            usuario pueda necesitar según su situación particular.
          </>,
          <>
            ZEDRO Academy puede ofrecer herramientas, estructura, principios y
            ejercicios prácticos, pero no garantiza resultados específicos. Los
            avances personales dependen de múltiples factores, incluyendo el
            contexto del usuario, su constancia, decisiones, aplicación práctica,
            circunstancias personales y acompañamiento externo cuando sea
            necesario.
          </>,
        ],
      },
      {
        title: "7. Membresías, pagos y cancelaciones",
        paragraphs: [
          <>
            ZEDRO Academy puede ofrecer productos digitales, membresías,
            suscripciones, periodos de prueba, contenidos formativos y servicios
            pagos. Las condiciones principales, precio, moneda, impuestos
            aplicables, frecuencia de cobro, periodo de prueba y periodo de
            acceso se informarán antes de confirmar la compra o suscripción.
          </>,
          <>
            Los pagos son procesados por proveedores externos, como Stripe.
            ZEDRO Academy no almacena números completos de tarjeta de crédito o
            débito.
          </>,
          <>
            La cancelación de una membresía detiene renovaciones y cobros
            futuros. Cuando ya exista un periodo mensual pagado, el usuario
            conservará el acceso hasta el final de dicho periodo, salvo que la
            ley aplicable exija o reconozca un derecho distinto.
          </>,
          <>
            Salvo que la ley aplicable exija lo contrario, los pagos ya
            realizados no son reembolsables por cancelaciones efectuadas antes de
            finalizar el periodo mensual ya cobrado y no se realizan reembolsos
            proporcionales por días no utilizados.
          </>,
          <>
            Las condiciones completas sobre membresías, prueba gratuita,
            cancelación, reembolsos, disputas de pago y contracargos se
            encuentran en los <Link to="/legal/terminos">Términos y Condiciones</Link>.
          </>,
        ],
      },
      {
        title: "8. Privacidad y datos personales",
        paragraphs: [
          <>
            ZEDRO Academy puede recopilar y almacenar información necesaria para
            operar la plataforma, incluyendo información de cuenta, estado de
            membresía, progreso, respuestas, bitácora, preferencias, registros
            de actividad, datos técnicos y comunicaciones de soporte.
          </>,
          <>
            El tratamiento de datos personales se describe en la{" "}
            <Link to="/legal/privacidad">Política de Privacidad</Link>. Al
            utilizar la plataforma, el usuario reconoce que cierta información
            puede ser almacenada en bases de datos, servicios de alojamiento,
            proveedores de pago, herramientas de correo, analítica, seguridad y
            otros proveedores tecnológicos necesarios para prestar el servicio.
          </>,
          <>
            ZEDRO Academy implementa medidas razonables de seguridad, pero
            ningún sistema digital puede garantizar seguridad, disponibilidad o
            recuperación absoluta frente a fallas técnicas, pérdida de acceso,
            ataques, errores humanos o incidentes fuera de control razonable.
          </>,
        ],
      },
      {
        title: "9. Asistencia basada en inteligencia artificial",
        paragraphs: [
          <>
            ZEDRO Academy puede incorporar funciones asistidas por inteligencia
            artificial para apoyar organización, reflexión, escritura, búsqueda,
            resumen o acompañamiento general dentro de la plataforma.
          </>,
          <>
            Los resultados generados con apoyo de IA pueden contener errores,
            imprecisiones o información incompleta. No deben interpretarse como
            asesoría profesional, médica, psicológica, legal, financiera,
            contable o pastoral personalizada.
          </>,
          <>
            Recomendamos no ingresar información altamente sensible o innecesaria
            en campos abiertos o herramientas asistidas por IA.
          </>,
        ],
      },
      {
        title: "10. Servicios de terceros",
        paragraphs: [
          <>
            La plataforma puede integrar o enlazar servicios de terceros, como
            procesadores de pago, proveedores de correo, alojamiento, bases de
            datos, almacenamiento, analítica, herramientas de seguridad,
            inteligencia artificial, soporte o recursos complementarios.
          </>,
          <>
            El uso de dichos servicios puede estar sujeto a sus propios términos,
            políticas de privacidad y medidas de seguridad. ZEDRO Academy no
            controla completamente las prácticas, disponibilidad o políticas de
            esos terceros, aunque procura trabajar con proveedores razonables y
            confiables para la operación de la plataforma.
          </>,
        ],
      },
      {
        title: "11. Disponibilidad y mantenimiento",
        paragraphs: [
          <>
            ZEDRO Academy procura mantener la plataforma disponible, segura y
            funcional. Sin embargo, no garantiza disponibilidad ininterrumpida,
            ausencia total de errores, compatibilidad perfecta con todos los
            dispositivos o conservación absoluta de todos los datos en cualquier
            circunstancia.
          </>,
          <>
            Podrán realizarse mantenimientos, mejoras, actualizaciones, cambios
            de diseño, ajustes técnicos o suspensión temporal de funcionalidades
            por razones operativas, legales, de seguridad o mejora del servicio.
          </>,
        ],
      },
      {
        title: "12. Limitación de responsabilidad",
        paragraphs: [
          <>
            En la máxima medida permitida por la ley aplicable, ZEDRO Academy,
            Académie ZEDRO, sus colaboradores, proveedores y representantes no
            serán responsables por daños indirectos, incidentales, especiales,
            pérdida de datos, pérdida de oportunidad, interrupciones, fallas
            técnicas, pérdida de acceso, decisiones tomadas por el usuario o
            resultados derivados del uso o imposibilidad de uso de la plataforma.
          </>,
          <>
            Nada en este Aviso legal limita derechos que no puedan ser limitados
            bajo la ley aplicable, incluyendo normas obligatorias de protección
            al consumidor, privacidad u otras disposiciones aplicables.
          </>,
        ],
      },
      {
        title: "13. Legislación aplicable",
        paragraphs: [
          <>
            Este Aviso legal se interpreta conforme a las leyes aplicables en la
            provincia de Québec y las leyes federales de Canadá que correspondan,
            sin perjuicio de los derechos obligatorios que puedan proteger al
            consumidor según su lugar de residencia.
          </>,
          <>
            En caso de inquietud, reclamo o desacuerdo, el usuario puede
            contactar primero a ZEDRO Academy para buscar una solución razonable
            antes de acudir a instancias formales, sin que ello limite derechos
            legales que no puedan ser renunciados.
          </>,
        ],
      },
      {
        title: "14. Actualizaciones",
        paragraphs: [
          <>
            ZEDRO Academy puede actualizar este Aviso legal para reflejar
            cambios legales, técnicos, operativos, comerciales o de
            funcionamiento de la plataforma. La versión vigente será la
            publicada en este sitio web con su fecha de actualización.
          </>,
        ],
      },
      {
        title: "15. Contacto",
        paragraphs: [
          <>Para consultas relacionadas con este Aviso legal, puedes escribir a:</>,
          <>
            Soporte: <a href={`mailto:${OPERATOR.supportEmail}`}>{OPERATOR.supportEmail}</a>
          </>,
          <>
            Administrativo/legal: <a href={`mailto:${OPERATOR.adminEmail}`}>{OPERATOR.adminEmail}</a>
          </>,
        ],
      },
    ],
  },
  fr: {
    title: "Avis légal",
    lastUpdatedLabel: "Dernière mise à jour :",
    lastUpdated: "28 juin 2026",
    intro: [
      <>
        Ce site Web, ses contenus, fonctionnalités, abonnements, ressources et
        services numériques font partie de <strong>{OPERATOR.platformName}</strong>,
        une plateforme exploitée sous le nom commercial enregistré{" "}
        <strong>{OPERATOR.legalTradeName}</strong> au {OPERATOR.jurisdictionFr}.
      </>,
      <>
        L’accès à <strong>{OPERATOR.website}</strong> et son utilisation
        impliquent l’acceptation du présent Avis légal, ainsi que des Conditions
        générales, de la Politique de confidentialité, de la Politique de
        cookies et des autres documents juridiques publiés sur la plateforme.
      </>,
    ],
    sections: [
      {
        title: "1. Identification du site et de l’exploitant",
        paragraphs: [
          <>
            Nom public de la plateforme : <strong>{OPERATOR.platformName}</strong>
          </>,
          <>
            Nom commercial enregistré : <strong>{OPERATOR.legalTradeName}</strong>
          </>,
          <>
            Adresse commerciale/postale : <strong>{OPERATOR.addressFr}</strong>
          </>,
          <>
            Lieu d’exploitation : <strong>{OPERATOR.jurisdictionFr}</strong>
          </>,
          <>
            Site Web : <strong>{OPERATOR.website}</strong>
          </>,
          <>
            Courriel d’assistance : <a href={`mailto:${OPERATOR.supportEmail}`}>{OPERATOR.supportEmail}</a>
          </>,
          <>
            Courriel administratif/juridique : <a href={`mailto:${OPERATOR.adminEmail}`}>{OPERATOR.adminEmail}</a>
          </>,
          <>
            ZEDRO Academy utilise ces canaux officiels pour les questions
            liées à l’accès, à l’abonnement, aux paiements, à l’annulation, à la
            confidentialité, à l’assistance et aux sujets juridiques.
          </>,
        ],
      },
      {
        title: "2. Propriété intellectuelle",
        paragraphs: [
          <>
            Tout le contenu disponible sur ZEDRO Academy, y compris les textes,
            noms, méthodologie, systèmes, modules, structure de formation,
            conception visuelle, logos, images, audios, ressources
            téléchargeables, interfaces, architecture de navigation, logiciel,
            code, matériel formatif et éléments de marque, est protégé par les
            règles applicables en matière de propriété intellectuelle et
            appartient à ZEDRO Academy, à Académie ZEDRO ou à leurs concédants
            respectifs.
          </>,
          <>
            Toute copie, reproduction, modification, distribution, publication,
            vente, sous-licence, exploitation commerciale ou utilisation non
            autorisée des contenus, de la méthodologie ou des documents de ZEDRO
            Academy est interdite, sauf autorisation préalable, expresse et
            écrite.
          </>,
        ],
      },
      {
        title: "3. Licence d’utilisation limitée",
        paragraphs: [
          <>
            L’accès à la plateforme, aux contenus et aux ressources de ZEDRO
            Academy est accordé au moyen d’une licence limitée, personnelle, non
            exclusive, non transférable, révocable et destinée à l’usage
            individuel de l’utilisateur dans la plateforme.
          </>,
          <>Cette licence permet :</>,
        ],
        list: [
          "D’accéder aux contenus inclus dans un abonnement actif.",
          "D’utiliser les ressources à des fins d’apprentissage, de réflexion et d’application personnelle.",
          "D’enregistrer la progression, les réponses et les registres dans le compte de l’utilisateur.",
          "De télécharger des documents uniquement lorsque la plateforme le permet expressément.",
        ],
        afterList: [
          <>
            <strong>Il n’est pas permis :</strong>
          </>,
        ],
        secondList: [
          "De partager des identifiants d’accès avec des tiers.",
          "De revendre, copier ou redistribuer les contenus de la plateforme.",
          "D’utiliser la méthodologie, les systèmes ou les documents à des fins commerciales, institutionnelles ou formatives externes sans autorisation écrite.",
          "D’extraire, copier ou reproduire la structure de la plateforme afin de créer des produits similaires.",
          "D’utiliser des automatisations, du moissonnage de données, de l’ingénierie inverse ou des outils pouvant affecter la sécurité ou le fonctionnement de la plateforme.",
        ],
      },
      {
        title: "4. Utilisation appropriée de la plateforme",
        paragraphs: [
          <>
            L’utilisateur s’engage à utiliser ZEDRO Academy de manière légale,
            respectueuse, responsable et conforme à l’objectif formatif de la
            plateforme.
          </>,
          <>Il n’est pas permis :</>,
        ],
        list: [
          "D’utiliser la plateforme à des fins frauduleuses, abusives, offensantes ou illégales.",
          "De tenter d’accéder à des comptes, données, systèmes ou zones non autorisés.",
          "D’introduire des virus, logiciels malveillants, code nuisible ou interférences techniques.",
          "De publier ou d’enregistrer du contenu portant atteinte aux droits de tiers.",
          "De poser des actions compromettant la sécurité, la stabilité ou la disponibilité de la plateforme.",
        ],
      },
      {
        title: "5. Langue du site et du contenu formatif",
        paragraphs: [
          <>
            Les informations publiques essentielles, les conditions d’achat et
            les pages juridiques de ZEDRO Academy peuvent être disponibles en
            espagnol et en français.
          </>,
          <>
            Le contenu formatif principal de l’écosystème ZEDRO Academy est
            actuellement offert en espagnol. En commençant un abonnement,
            l’utilisateur comprend que l’expérience formative principale est
            actuellement offerte en espagnol, même si les informations
            juridiques et commerciales essentielles peuvent aussi être
            disponibles en français.
          </>,
        ],
      },
      {
        title: "6. Contenu formatif et limites",
        paragraphs: [
          <>
            Les contenus de ZEDRO Academy ont une finalité formative,
            éducative, réflexive et informative. Ils ne remplacent pas un avis
            médical, psychologique, juridique, financier, thérapeutique,
            comptable, pastoral personnalisé ni tout autre accompagnement
            professionnel dont l’utilisateur pourrait avoir besoin selon sa
            situation particulière.
          </>,
          <>
            ZEDRO Academy peut offrir des outils, une structure, des principes
            et des exercices pratiques, mais ne garantit aucun résultat précis.
            Les progrès personnels dépendent de plusieurs facteurs, notamment le
            contexte de l’utilisateur, sa constance, ses décisions, son
            application pratique, ses circonstances personnelles et, au besoin,
            un accompagnement externe approprié.
          </>,
        ],
      },
      {
        title: "7. Abonnements, paiements et annulations",
        paragraphs: [
          <>
            ZEDRO Academy peut offrir des produits numériques, abonnements,
            périodes d’essai, contenus formatifs et services payants. Les
            principales conditions, le prix, la devise, les taxes applicables,
            la fréquence de facturation, la période d’essai et la période
            d’accès seront indiqués avant la confirmation de l’achat ou de
            l’abonnement.
          </>,
          <>
            Les paiements sont traités par des fournisseurs externes, comme
            Stripe. ZEDRO Academy ne conserve pas les numéros complets de carte
            de crédit ou de débit.
          </>,
          <>
            L’annulation d’un abonnement met fin aux renouvellements et aux
            paiements futurs. Lorsqu’une période mensuelle a déjà été payée,
            l’utilisateur conserve l’accès jusqu’à la fin de cette période, sauf
            si la loi applicable exige ou reconnaît un droit différent.
          </>,
          <>
            Sauf si la loi applicable l’exige autrement, les paiements déjà
            effectués ne sont pas remboursables en cas d’annulation avant la fin
            de la période mensuelle déjà facturée, et aucun remboursement
            proportionnel n’est effectué pour les jours non utilisés.
          </>,
          <>
            Les conditions complètes concernant les abonnements, l’essai
            gratuit, l’annulation, les remboursements, les litiges de paiement
            et les rétrofacturations se trouvent dans les{" "}
            <Link to="/legal/terminos">Conditions générales</Link>.
          </>,
        ],
      },
      {
        title: "8. Confidentialité et renseignements personnels",
        paragraphs: [
          <>
            ZEDRO Academy peut recueillir et conserver les renseignements
            nécessaires au fonctionnement de la plateforme, notamment les
            renseignements de compte, l’état de l’abonnement, la progression,
            les réponses, la bitácora, les préférences, les registres
            d’activité, les données techniques et les communications
            d’assistance.
          </>,
          <>
            Le traitement des renseignements personnels est décrit dans la{" "}
            <Link to="/legal/privacidad">Politique de confidentialité</Link>. En
            utilisant la plateforme, l’utilisateur reconnaît que certaines
            informations peuvent être conservées dans des bases de données, des
            services d’hébergement, des fournisseurs de paiement, des outils de
            courriel, d’analytique, de sécurité et d’autres fournisseurs
            technologiques nécessaires à la prestation du service.
          </>,
          <>
            ZEDRO Academy met en œuvre des mesures de sécurité raisonnables,
            mais aucun système numérique ne peut garantir une sécurité, une
            disponibilité ou une récupération absolue en cas de défaillance
            technique, perte d’accès, attaque, erreur humaine ou incident hors
            de son contrôle raisonnable.
          </>,
        ],
      },
      {
        title: "9. Assistance fondée sur l’intelligence artificielle",
        paragraphs: [
          <>
            ZEDRO Academy peut intégrer des fonctionnalités assistées par
            intelligence artificielle afin de soutenir l’organisation, la
            réflexion, l’écriture, la recherche, le résumé ou l’accompagnement
            général dans la plateforme.
          </>,
          <>
            Les résultats générés avec le soutien de l’IA peuvent contenir des
            erreurs, des imprécisions ou des renseignements incomplets. Ils ne
            doivent pas être interprétés comme un avis professionnel, médical,
            psychologique, juridique, financier, comptable ou pastoral
            personnalisé.
          </>,
          <>
            Nous recommandons de ne pas saisir de renseignements très sensibles
            ou non nécessaires dans les champs ouverts ou les outils assistés
            par IA.
          </>,
        ],
      },
      {
        title: "10. Services de tiers",
        paragraphs: [
          <>
            La plateforme peut intégrer ou lier des services de tiers, tels que
            des processeurs de paiement, fournisseurs de courriel, hébergement,
            bases de données, stockage, analytique, outils de sécurité,
            intelligence artificielle, assistance ou ressources
            complémentaires.
          </>,
          <>
            L’utilisation de ces services peut être soumise à leurs propres
            conditions, politiques de confidentialité et mesures de sécurité.
            ZEDRO Academy ne contrôle pas entièrement les pratiques, la
            disponibilité ou les politiques de ces tiers, bien qu’elle cherche à
            travailler avec des fournisseurs raisonnables et fiables pour le
            fonctionnement de la plateforme.
          </>,
        ],
      },
      {
        title: "11. Disponibilité et maintenance",
        paragraphs: [
          <>
            ZEDRO Academy cherche à maintenir la plateforme disponible,
            sécurisée et fonctionnelle. Toutefois, elle ne garantit pas une
            disponibilité ininterrompue, l’absence totale d’erreurs, une
            compatibilité parfaite avec tous les appareils ni la conservation
            absolue de toutes les données en toute circonstance.
          </>,
          <>
            Des maintenances, améliorations, mises à jour, changements de
            conception, ajustements techniques ou suspensions temporaires de
            certaines fonctionnalités peuvent être effectués pour des raisons
            opérationnelles, juridiques, de sécurité ou d’amélioration du
            service.
          </>,
        ],
      },
      {
        title: "12. Limitation de responsabilité",
        paragraphs: [
          <>
            Dans la mesure maximale permise par la loi applicable, ZEDRO
            Academy, Académie ZEDRO, ses collaborateurs, fournisseurs et
            représentants ne peuvent être tenus responsables des dommages
            indirects, accessoires ou spéciaux, de la perte de données, de la
            perte d’occasion, des interruptions, des défaillances techniques, de
            la perte d’accès, des décisions prises par l’utilisateur ou des
            résultats découlant de l’utilisation ou de l’impossibilité
            d’utiliser la plateforme.
          </>,
          <>
            Rien dans le présent Avis légal ne limite les droits qui ne peuvent
            être limités en vertu de la loi applicable, y compris les règles
            obligatoires de protection du consommateur, de confidentialité ou
            toute autre disposition applicable.
          </>,
        ],
      },
      {
        title: "13. Loi applicable",
        paragraphs: [
          <>
            Le présent Avis légal est interprété conformément aux lois
            applicables dans la province de Québec et aux lois fédérales du
            Canada applicables, sans préjudice des droits obligatoires pouvant
            protéger le consommateur selon son lieu de résidence.
          </>,
          <>
            En cas de question, réclamation ou désaccord, l’utilisateur peut
            d’abord communiquer avec ZEDRO Academy afin de rechercher une
            solution raisonnable avant de recourir à des instances formelles,
            sans que cela limite les droits légaux auxquels il ne peut renoncer.
          </>,
        ],
      },
      {
        title: "14. Mises à jour",
        paragraphs: [
          <>
            ZEDRO Academy peut mettre à jour le présent Avis légal afin de
            refléter des changements juridiques, techniques, opérationnels,
            commerciaux ou liés au fonctionnement de la plateforme. La version
            en vigueur est celle publiée sur ce site Web avec sa date de mise à
            jour.
          </>,
        ],
      },
      {
        title: "15. Contact",
        paragraphs: [
          <>Pour toute question concernant le présent Avis légal, vous pouvez écrire à :</>,
          <>
            Assistance : <a href={`mailto:${OPERATOR.supportEmail}`}>{OPERATOR.supportEmail}</a>
          </>,
          <>
            Administratif/juridique : <a href={`mailto:${OPERATOR.adminEmail}`}>{OPERATOR.adminEmail}</a>
          </>,
        ],
      },
    ],
  },
};

function renderParagraphs(paragraphs = []) {
  return paragraphs.map((paragraph, index) => (
    <p key={index}>{paragraph}</p>
  ));
}

function renderList(items = []) {
  if (!items.length) return null;

  return (
    <ul>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

export default function LegalNotice() {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      } else {
        setLanguage("es");
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const copy = LEGAL_NOTICE_COPY[language] || LEGAL_NOTICE_COPY.es;

  return (
    <div style={pageStyle}>
      <h1>{copy.title}</h1>

      <p>
        <strong>{copy.lastUpdatedLabel}</strong> {copy.lastUpdated}
      </p>

      {renderParagraphs(copy.intro)}

      {copy.sections.map((section) => (
        <section key={section.title}>
          <h2 style={sectionTitleStyle}>{section.title}</h2>
          {renderParagraphs(section.paragraphs)}
          {renderList(section.list)}
          {renderParagraphs(section.afterList)}
          {renderList(section.secondList)}
        </section>
      ))}
    </div>
  );
}
